import { INTERVIEW_OUTCOMES, type RecordInterviewOutcomeRequest } from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';

import { useRecordInterviewOutcomeMutation } from '../../services/verification/verification.api';

export interface UseRecordInterviewOutcomeModalLogic {
  caseId: string | null;
  fields: FieldDef[];
  initialValues: Partial<RecordInterviewOutcomeRequest>;
  submit: (values: RecordInterviewOutcomeRequest) => Promise<void>;
}

/**
 * Recording what happened at the interview.
 *
 * ## It records; it does not decide
 *
 * The `interview` **step** reaches `passed` or `failed` through the step
 * verdicts on the checklist, by a human. This writes the outcome on the
 * interview row and the private notes beside it, and the dialog's own subtitle
 * says so — an operator who assumed recording "failed" had refused the step
 * would leave a case sitting in the queue waiting for a verdict nobody knew was
 * outstanding.
 *
 * ## `notes` is nullable and required, like `locationOrLink`
 *
 * `RecordInterviewOutcomeSchema` types it `.trim().min(1).max(2000).nullable()`,
 * so an empty box means `null` — `isEmptyNull`, for exactly the reason the
 * schedule dialog gives.
 *
 * `rescheduled` is on the list deliberately: it is an outcome rather than a
 * deletion. The appointment happened to the extent that somebody turned up to
 * move it, and a member rescheduled twice is a fact a reviewer should be able to
 * see.
 */
export function useRecordInterviewOutcomeModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseRecordInterviewOutcomeModalLogic {
  const { t } = useTranslation('verification');
  const toast = useToast();

  const [recordOutcome] = useRecordInterviewOutcomeMutation();

  const caseId = readString(modalData?.['caseId']);
  const outcome = readString(modalData?.['outcome']);
  const notes = readString(modalData?.['notes']);

  const fields = useMemo<FieldDef[]>(
    () => [
      {
        name: 'outcome',
        kind: 'select',
        label: t('interview.record.outcome'),
        isRequired: true,
        options: INTERVIEW_OUTCOMES.map((value) => ({
          value,
          label: t(`interview.outcomeValue.${value}`),
        })),
        colSpan: 'full',
      },
      {
        name: 'notes',
        kind: 'textarea',
        label: t('interview.record.notes'),
        description: t('interview.record.notesHint'),
        maxLength: 2000,
        rows: 6,
        isEmptyNull: true,
        colSpan: 'full',
      },
    ],
    [t],
  );

  const submit = useCallback(
    async (values: RecordInterviewOutcomeRequest) => {
      if (caseId === null) return;

      // Rejecting is the contract with `DynamicForm` — it maps the refusal onto
      // the fields and the dialog stays open showing it.
      await recordOutcome({ id: caseId, ...values }).unwrap();

      // A passed interview is the verdict (F7); the refetched case says the rest.
      toast.success(
        t(values.outcome === 'passed' ? 'interview.record.passed' : 'interview.record.success'),
      );
      onClose();
    },
    [caseId, onClose, recordOutcome, t, toast],
  );

  return {
    caseId,
    fields,
    initialValues: {
      ...(outcome === null ? {} : { outcome: readOutcome(outcome) }),
      notes,
    },
    submit,
  };
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Narrows a payload's outcome. An unknown one seeds nothing rather than a cast. */
function readOutcome(value: string): RecordInterviewOutcomeRequest['outcome'] | undefined {
  return INTERVIEW_OUTCOMES.find((outcome) => outcome === value);
}
